import React, { useState, useEffect } from "react";
import {
    Box,
    Flex,
    Text,
    Badge,
    useDisclosure,
} from "@chakra-ui/react";
import { Plus, Eye, Archive, EllipsisVertical, ShieldAlert } from "lucide-react";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";
import AddPolicyDrawer from "./components/AddTermsDrawer";
import ViewPolicyDrawer from "./components/ViewPolicyDrawer";
import EditPolicyDrawer from "./components/EditPolicyDrawer";
import ArchivePolicyModal from "./components/ArchivePolicyModal";
import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from "components/Toasts/OpenNotification";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchPoliciesAction,
    addPolicyAction,
    updatePolicyAction,
    deletePolicyAction,
} from "../../../../state/actions/termsAndPoliciesActions";

const TermsPoliciesTab = () => {
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const { policies, loading, error } = useSelector((state) => state.terms);
    const { isOpen: isAddOpen, onOpen: openAdd, onClose: closeAdd } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: openEdit, onClose: closeEdit } = useDisclosure();
    const { isOpen: isArchiveOpen, onOpen: openArchive, onClose: closeArchive } = useDisclosure();
    const showToast = useCustomNotification();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchPoliciesAction());
    }, [dispatch]);

    // Handle Add Policy
    const handleAddPolicy = async (newPolicy) => {
        try {
            if (!newPolicy.title) {
                throw new Error("Policy title is required");
            }

            const result = await dispatch(addPolicyAction(newPolicy));
            if (result.success) {
                showToast({
                    message: "Policy Added",
                    description: "The policy has been successfully added.",
                    type: "success",
                });
                dispatch(fetchPoliciesAction());
                closeAdd();
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            showToast({
                message: "Failed to Add Policy",
                description: error.message,
                type: "error",
            });
        }
    };

    // Handle Edit Policy
    const handleEditPolicy = async (updatedPolicy) => {
        try {
            if (!updatedPolicy.title) {
                throw new Error("Policy title is required");
            }

            const result = await dispatch(updatePolicyAction(updatedPolicy.id, updatedPolicy));
            if (result.success) {
                showToast({
                    message: "Policy Updated",
                    description: "The policy has been successfully updated.",
                    type: "success",
                });
                dispatch(fetchPoliciesAction());
                closeEdit();
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            showToast({
                message: "Failed to Update Policy",
                description: error.message,
                type: "error",
            });
        }
    };

    // Handle Delete Policy
    const handleDeletePolicy = async (policyId) => {
        try {
            const result = await dispatch(deletePolicyAction(policyId));
            if (result.success) {
                showToast({
                    message: "Policy Deleted",
                    description: "The policy has been successfully deleted.",
                    type: "success",
                });
                dispatch(fetchPoliciesAction());
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            showToast({
                message: "Failed to Delete Policy",
                description: error.message,
                type: "error",
            });
        }
    };


    const handleArchivePolicy = (policy) => {
        setSelectedPolicy(policy);
        openArchive();
    };

    const handleConfirmArchive = async () => {
        try {
            if (!selectedPolicy) {
                throw new Error("No policy selected for archiving");
            }

            const result = await dispatch(updatePolicyAction(selectedPolicy.id, { ...selectedPolicy, isArchived: true }));
            if (result.success) {
                showToast({
                    message: "Policy Archived",
                    description: "The policy has been successfully archived.",
                    type: "success",
                });
                closeArchive();
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            showToast({
                message: "Failed to Archive Policy",
                description: error.message,
                type: "error",
            });
        }
    };



    const generateMenuItems = (row) => [
        {
            icon: <Eye size={16} />,
            text: "View",
            onClick: () => {
                setSelectedPolicy(row.original);
                openEdit();
            },
        },
        {
            icon: <Archive size={16} />,
            text: "Archive",
            onClick: () => handleDeletePolicy(row.original.id),
            extraIcon: <ShieldAlert size={16} color="pink" />,
        },
    ];

    const columnsData = [
        {
            Header: "Title",
            accessor: "title",
        },
        {
            Header: "Date Added",
            accessor: "created_at",
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: "Updated At",
            accessor: "updated_at",
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: "Archived",
            accessor: "isArchived",
            Cell: ({ value }) => (
                <Badge colorScheme={value ? "red" : "green"} borderRadius="full" px={2}>
                    {value ? "Archived" : "Active"}
                </Badge>
            ),
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={20} color="grey" />}
                    items={generateMenuItems(row)}
                />
            ),
        },
    ];

    return (
        <Box>
            <Flex justifyContent="end" mb={4}>
                {loading ? (
                    // Show Skeleton when loading
                    <Skeleton height="40px" width="200px" borderRadius="12px" />
                ) : (
                    // Show the actual button when not loading
                    <GradientButton
                        icon={<Plus size={16} />}
                        height={"40px"}
                        mr={5}
                        onClick={openAdd}
                    >
                        Add New Policy
                    </GradientButton>
                )}
            </Flex>
            <ReusableTable
                columnsData={columnsData}
                tableData={Array.isArray(policies.data) ? policies.data : []}
                loading={loading}
            />


            {/* Drawers and Modal */}
            <AddPolicyDrawer isOpen={isAddOpen} onClose={closeAdd} onAddPolicy={handleAddPolicy} />
            <EditPolicyDrawer isOpen={isEditOpen} onClose={closeEdit} policy={selectedPolicy} onUpdatePolicy={handleEditPolicy} />
            <ArchivePolicyModal isOpen={isArchiveOpen} onClose={closeArchive} onConfirm={handleConfirmArchive} policy={selectedPolicy} />
        </Box>
    );
};

export default TermsPoliciesTab;
